/**
 * Whether the active brand's domain may have mail, as ows-mail says on `GET /domains/{domain}`.
 * Mailbox shows only on an answer that says it may (`eligible`: the domain, or `not_found`, a domain
 * not registered yet, which the first create does). Everything else keeps it hidden: `not_eligible`
 * (a domain that will never have mailboxes), a read that failed or is still out, the brand list still
 * loading, a brand with no domain on record. Hidden means the Settings index has no Mailbox row, the
 * section and the mailbox page bounce to the Settings index, and FTUX's mail piece is not offered.
 * Mailbox never appears and then goes.
 *
 * Asked only where the row could show: the `mailbox` flag on and ows-mail configured (the fixtures and
 * an environment with no mail service ask nothing and are not gated here). One read per domain for
 * the page's lifetime, shared by every caller: the first reader to mount asks (the FTUX probe from app
 * load while a run is open, else Settings), and the others reuse its answer. Keyed by domain, so a
 * brand switch never reads the previous brand's answer.
 *
 * A failed read is asked again: by each reader that mounts after it, by a reader once the owner's token
 * changes (ows-mail answers an expired token with 401 and the token is refreshed later, elsewhere), and
 * every ELIGIBILITY_REREAD_MS while a reader stays mounted (the FTUX probe stays for the whole run). A
 * read still out under an older token is asked again under the new one, and only the newest read for a
 * domain lands, so a late 401 for the old token never counts. A failure a reader has not asked again
 * itself, under its own token, reads as checking, so a page never bounces on an answer that is about
 * to be replaced.
 */
import React from 'react';

import { useAppSelector } from '@/store';

import { useFeatureGate } from '../../hooks/useFeatureGate';
import { MAILBOX_FALLBACK_DOMAIN } from '../../mock-data/mailbox-records';
import { useBrandSettled } from '../website/useBrandSettled';
import { fetchDomain } from './api/client';
import { MAILBOX_SOURCE } from './api/source';
import { mailboxEligibilityOf, mailDomainOf, type MailboxEligibilityRead } from './state';

/**
 * `eligible` is the only one that shows Mailbox. `checking`: the answer is not in yet. `unknown`: no
 * answer to go by (the read failed, or the brand has no domain to ask about).
 */
export type MailboxEligibility = 'checking' | 'eligible' | 'not_eligible' | 'unknown';

/** How long a failed read waits before a mounted reader asks again (ms). */
export const ELIGIBILITY_REREAD_MS = 30_000;

/** A domain's latest read: its answer (or `checking` while it is out) and the key it was asked under. */
type EligibilityEntry = { answer: MailboxEligibilityRead | 'checking'; key: string };

const entries = new Map<string, EligibilityEntry>();
/** The newest read per domain: only its answer lands. */
const newestRead = new Map<string, number>();
let readCount = 0;
const listeners = new Set<() => void>();

const settle = (domain: string, entry: EligibilityEntry): void => {
  entries.set(domain, entry);
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Asks ows-mail about `domain` under `key` (the domain and the owner's token the request carries). */
const ask = (domain: string, key: string): void => {
  readCount += 1;
  const read = readCount;
  newestRead.set(domain, read);
  settle(domain, { answer: 'checking', key });
  const land = (answer: MailboxEligibilityRead): void => {
    if (newestRead.get(domain) === read) settle(domain, { answer, key });
  };
  fetchDomain(domain).then(
    () => land('eligible'),
    (rejection: unknown) => land(mailboxEligibilityOf(rejection)),
  );
};

export const useMailboxEligibility = (): MailboxEligibility => {
  const { isEnabled } = useFeatureGate();
  const brandSettled = useBrandSettled();
  const domain = mailDomainOf(useAppSelector((state) => state.casinoInstance.displayBrand?.casino_data?.details?.domain));
  const token = useAppSelector((state) => state.authentication.access_token);
  const live = isEnabled('mailbox') && MAILBOX_SOURCE === 'http';
  const asks = live && brandSettled && domain !== MAILBOX_FALLBACK_DOMAIN;
  // What this reader asks under (its domain and the owner's token): a failure found under anything else
  // is not its own yet.
  const readKey = `${domain}\n${token ?? ''}`;
  const [askedUnder, setAskedUnder] = React.useState<string | null>(null);

  // Syncs with ows-mail: one read per domain. A failed one is asked again by each new reader and on a new
  // token, and so is a read still out under an older token.
  React.useEffect(() => {
    if (!asks || askedUnder === readKey) return;
    const known = entries.get(domain);
    if (
      known === undefined ||
      known.answer === 'failed' ||
      (known.answer === 'checking' && known.key !== readKey)
    ) {
      ask(domain, readKey);
    }
    setAskedUnder(readKey);
  }, [asks, domain, readKey, askedUnder]);

  const entry = React.useSyncExternalStore(subscribe, () => (asks ? entries.get(domain) : undefined));
  const failed = asks && entry?.answer === 'failed';

  // A failed read is asked again a while later, and again after each attempt that fails, while this reader
  // stays mounted. The first reader whose timer fires asks; the others then find it checking.
  React.useEffect(() => {
    if (!failed) return undefined;
    const timer = window.setTimeout(() => {
      if (entries.get(domain)?.answer === 'failed') ask(domain, readKey);
    }, ELIGIBILITY_REREAD_MS);
    return () => window.clearTimeout(timer);
  }, [failed, domain, readKey]);

  // The fixtures, an environment with no mail service, or the flag off: nothing to ask, not gated here.
  if (!live) return 'eligible';
  if (!brandSettled) return 'checking';
  if (domain === MAILBOX_FALLBACK_DOMAIN) return 'unknown';
  if (entry === undefined || entry.answer === 'checking') return 'checking';
  // A failure counts only once this reader has asked under its own key (until then its effect is about to).
  if (entry.answer === 'failed') return askedUnder === readKey && entry.key === readKey ? 'unknown' : 'checking';
  return entry.answer;
};
